import { Controller } from "@hotwired/stimulus";
import {
  errorDetail,
  formatPln,
  getJSON,
  listResults,
  sendJSON,
} from "../api.js";

// Koszyk: wysuwany drawer zasilany z DRF (GET /api/cart/) + add/remove/checkout.
//
// Scope kontrolera = <body> strony (block body_attrs) — dzięki temu przycisk
// koszyka w nav i przyciski "+ Dodaj" w wierszach tabeli trafiają w akcje.
// Mutacje NIE mają fallbacku "udawanego sukcesu": błąd POST = brak zmiany UI
// (+ komunikat), bo koszyk to rezerwacje i pieniądze.
export default class extends Controller {
  static targets = [
    "overlay",
    "drawer",
    "count",
    "empty",
    "content",
    "success",
    "net",
    "vat",
    "gross",
    "items",
    "error",
    "phoneNotice",
    "checkoutBtn",
    "walletOptOut",
    "digitalConsent",
    "termsModal",
    "acceptTermsBtn",
    // smoke-0909#3: add-mode modal targets
    "modeModal",
    "modeCount",
    "modeOptExcl",
    "modeOptSlot",
    "modeConfirm",
  ];
  static values = {
    count: Number,
    url: String, // GET  /api/cart/
    addUrl: String, // POST /api/cart/add/      {lead, subcategory} — kafelek (pk pary); backend (reserve_tile) dobiera pasujący tag-listing
    removeUrl: String, // POST /api/cart/remove/   {listing_id}
    checkoutUrl: String, // POST /api/orders/checkout/
    acceptTermsUrl: String, // POST /api/terms/accept/
  };

  checkingOut = false;
  acceptingTerms = false;

  connect() {
    // Po powrocie z płatności: ?payment=success => pokaż potwierdzenie (nie nadpisuj loadem).
    const params = new URLSearchParams(window.location.search);
    this.showingSuccess = params.get("payment") === "success";
    if (this.showingSuccess) {
      this.open();
      this.#showState("success");
      // Zdejmij parametr z URL — reload/nawigacja wstecz nie ma ponownie pokazywać sukcesu.
      params.delete("payment");
      const query = params.toString();
      window.history.replaceState(
        {},
        "",
        query ? `?${query}` : window.location.pathname,
      );
    }
    if (this.urlValue) this.load();
  }

  async load() {
    try {
      this.#render(await getJSON(this.urlValue));
    } catch {
      // np. 400 "Brak organizacji" — zostaw stan pusty; strona dalej działa.
    }
  }

  open() {
    if (this.hasOverlayTarget) {
      this.overlayTarget.hidden = false;
      this.drawerTarget.hidden = false;
    }
  }

  close() {
    if (this.hasOverlayTarget) {
      this.overlayTarget.hidden = true;
      this.drawerTarget.hidden = true;
    }
    // Zamknięcie potwierdzenia = koniec stanu sukcesu — drawer wraca do koszyka (nowe zakupy możliwe).
    if (this.showingSuccess) {
      this.showingSuccess = false;
      this.#updatePurchaseControls();
      this.#showState(this.countValue > 0 ? "content" : "empty");
    }
  }

  // Enables/disables the checkout button based on the digital-consent checkbox state.
  toggleConsent() {
    this.#updatePurchaseControls();
  }

  // "+ Dodaj" w wierszu ORAZ w modalu podglądu (delegacja klikiem — patrz
  // preview_controller.js#addToCart). Kafelek = (lead, subcategory), OBA pk z
  // data-cart-lead-param / data-cart-subcategory-param. Katalog jest add-only: kafelek
  // może odpowiadać >1 tag-listingowi, więc "cofnięcie" tego samego kliknięcia nie ma
  // jednoznacznego listing_id do usunięcia — usuwanie robi się z drawera koszyka
  // (cart#remove, per-listing, patrz #render/#reflectButton niżej).
  //
  // smoke-0909#3: jeśli wiersz ma slot_available=true (param), otwiera modal wyboru trybu
  // zamiast od razu POST-ować. BUR (bez slot_available) dodaje bezpośrednio — kontrakt bez
  // zmiany (backend domyślnie wybiera wyłączność, mode nie trafia do body).
  async add(event) {
    const { lead, subcategory, slotAvailable, exclPrice, slotPrice, remaining, exclAvailable } = event.params;
    const btn = event.currentTarget;
    if (slotAvailable) {
      this.#openMode({
        type: "single",
        lead,
        subcategory,
        exclPrice: exclPrice ?? null,
        slotPrice: slotPrice ?? null,
        remaining: remaining ?? 0,
        exclAvailable: exclAvailable !== false,
        btn,
      });
      return;
    }
    // BUR / brak slotów: POST bezpośrednio bez trybu (kontrakt pre-modal zachowany).
    const body = { lead, subcategory };
    const res = await sendJSON("POST", this.addUrlValue, body);
    if (!res.ok) {
      this.#flashError(
        errorDetail(res.data, "Nie udało się zaktualizować koszyka."),
      );
      return;
    }
    this.#reflectButton(btn, true);
    await this.load();
  }

  // --- modal wyboru trybu (smoke-0909#3) ---

  // Wywoływany przez lead_table_controller gdy użytkownik klika „Dodaj zaznaczone".
  // listingIds: tablica id-ów listingów; addBulkUrl: endpoint add-bulk; onDone: callback po
  // zakończeniu (lead_table używa go do wyczyszczenia zaznaczenia).
  openModeBulk(listingIds, addBulkUrl, onDone) {
    this.#openMode({
      type: "bulk",
      listingIds,
      addBulkUrl,
      onDone,
      exclAvailable: true,
    });
  }

  // Zamknięcie modalu (klik w overlay lub przycisk Anuluj).
  closeMode() {
    if (!this.hasModeModalTarget) return;
    if (this._modeRaf) cancelAnimationFrame(this._modeRaf);
    this.modeModalTarget.classList.remove("is-open");
    setTimeout(() => {
      if (this.hasModeModalTarget && !this.modeModalTarget.classList.contains("is-open"))
        this.modeModalTarget.hidden = true;
    }, 180);
    this._pendingMode = null;
  }

  // Zatrzymuje propagację kliknięcia z treści modalu do nakładki (która zamknęłaby modal).
  stopMode(event) {
    event.stopPropagation();
  }

  // Potwierdzenie wyboru trybu — POST do odpowiedniego endpointu.
  async confirmMode() {
    if (!this._pendingMode) return;
    const mode =
      this.hasModeOptSlotTarget && this.modeOptSlotTarget.classList.contains("is-active")
        ? "slot"
        : "exclusivity";
    const pending = this._pendingMode;
    this.closeMode();

    if (pending.type === "bulk") {
      const res = await sendJSON("POST", pending.addBulkUrl, { listing_ids: pending.listingIds, mode });
      const added = res.ok ? (res.data?.added ?? []) : [];
      // Odbij wyniki per-wiersz — identyczna logika jak w poprzednim lead_table#addSelected.
      pending.listingIds.forEach((id) => {
        const btn = document.querySelector(`.ls-add-btn[data-cart-id-param="${id}"]`);
        if (!btn) return;
        if (added.includes(id)) {
          if (!btn.classList.contains("is-in-cart")) {
            btn.classList.add("is-in-cart");
            const mono = btn.querySelector(".ls-mono");
            const label = btn.querySelector("[data-cart-label]");
            if (mono) mono.textContent = "\u2713";
            if (label) label.textContent = "W koszyku";
            const row = btn.closest(".ls-lead-row");
            if (row) row.classList.add("is-selected");
          }
        } else {
          btn.disabled = true;
          btn.classList.add("is-rejected");
          btn.title = "Nie dodano \u2014 wybrany tryb zakupu nie jest ju\u017c dost\u0119pny dla tego leada.";
          const mono = btn.querySelector(".ls-mono");
          const label = btn.querySelector("[data-cart-label]");
          if (mono) mono.textContent = "\u00d7";
          if (label) label.textContent = "Niedost\u0119pny";
          const row = btn.closest(".ls-lead-row");
          if (row) row.classList.add("is-rejected");
        }
      });
      if (pending.onDone) pending.onDone();
      await this.load();
      if (res.ok && res.data?.cart_full) this.#flashError(res.data.detail);
    } else {
      // single
      const body = { lead: pending.lead, subcategory: pending.subcategory, mode };
      const res = await sendJSON("POST", this.addUrlValue, body);
      if (!res.ok) {
        this.#flashError(errorDetail(res.data, "Nie uda\u0142o si\u0119 zaktualizowa\u0107 koszyka."));
        return;
      }
      if (pending.btn) this.#reflectButton(pending.btn, true);
      await this.load();
    }
  }

  // Prze\u0142\u0105czenie aktywnej opcji trybu w modalu (Wy\u0142\u0105czno\u015b\u0107 / Slot).
  selectModeOpt(event) {
    const mode = event.currentTarget.dataset.mode;
    if (this.hasModeOptExclTarget)
      this.modeOptExclTarget.classList.toggle("is-active", mode === "exclusivity");
    if (this.hasModeOptSlotTarget)
      this.modeOptSlotTarget.classList.toggle("is-active", mode === "slot");
  }

  // Podpi\u0119cie akcji do przycisk\u00f3w modalnych przy po\u0142\u0105czeniu targetu.
  modeOptExclTargetConnected(el) {
    el.dataset.action = "click->cart#selectModeOpt";
  }

  modeOptSlotTargetConnected(el) {
    el.dataset.action = "click->cart#selectModeOpt";
  }

  modeConfirmTargetConnected(el) {
    el.dataset.action = "cart#confirmMode";
  }

  async remove(event) {
    const id = event.params.id;
    const res = await sendJSON("POST", this.removeUrlValue, { listing_id: id });
    if (!res.ok) return;
    document.dispatchEvent(new CustomEvent("cart:removed", {
      detail: {
        listing_id: Number(id),
        lead_id: Number(event.params.lead),
        subcategory_id: Number(event.params.subcategory),
      },
    }));
    await this.load();
  }

  async checkout() {
    if (this.checkingOut || this.acceptingTerms) return;
    // art. 38 ust. 1 pkt 13: refuse to proceed if the digital-content consent box is unchecked.
    if (!this.#consentGiven()) return;
    this.checkingOut = true;
    this.#updatePurchaseControls();
    // Portfel domyślnie WŁĄCZONY; checkbox „Nie używaj portfela" = świadomy opt-out.
    const skipWallet = this.hasWalletOptOutTarget && this.walletOptOutTarget.checked;
    const res = await sendJSON("POST", this.checkoutUrlValue, {
      use_wallet: !skipWallet,
      digital_content_consent: true,
    });
    if (res.ok) {
      if (res.data?.redirect_url) {
        // Zewnętrzna bramka (P24): jedziemy na stronę płatności.
        window.location.assign(res.data.redirect_url);
        return;
      }
      // Brak redirectu = płatność uznana od razu (dev AutoPaid / kredyt) — pokaż potwierdzenie.
      this.showingSuccess = true;
      this.#showState("success");
      this.open();
      await this.load(); // koszyk jest już pusty (rezerwacje sprzedane)
      this.checkingOut = false;
      this.#updatePurchaseControls();
      return;
    }
    this.checkingOut = false;
    this.#updatePurchaseControls();
    if (res.status === 403 && res.data?.code === "phone_verification_required") {
      if (this.hasPhoneNoticeTarget) this.phoneNoticeTarget.hidden = false;
      this.#flashError(errorDetail(res.data, "Potwierdź numer telefonu przed zakupem."));
      // Prowadzimy prosto na weryfikację; po potwierdzeniu strona telefonu wraca na ten adres (?next=).
      const verifyUrl = this.#phoneVerifyUrl(res.data?.verify_url);
      if (verifyUrl) this.navigate(verifyUrl);
      return;
    }
    // Brama regulaminu (art. 7 RODO): checkout omija middleware /api/, więc backstop jest tu.
    // 403 z code=terms_required → pokaż modal regulaminu zamiast generycznego błędu.
    if (res.status === 403 && res.data?.code === "terms_required") {
      if (this.hasTermsModalTarget) this.termsModalTarget.hidden = false;
      return;
    }
    // 400 code=digital_content_consent_required — render server detail (belt-and-suspenders).
    if (res.status === 400 && res.data?.code === "digital_content_consent_required") {
      await this.load();
      this.#flashError(errorDetail(res.data, "Wymagana zgoda na treści cyfrowe."));
      return;
    }
    await this.load(); // rezerwacje mogły wygasnąć — odśwież zawartość
    this.#flashError(
      errorDetail(res.data, "Nie udało się rozpocząć płatności."),
    ); // PO renderze (render czyści stare błędy)
  }

  // Szew testowy: jedyne miejsce, gdzie kontroler nawiguje po błędzie checkoutu (jsdom nie implementuje location.assign).
  navigate(url) {
    window.location.assign(url);
  }

  // `verify_url` z odpowiedzi 403 (users:phone_verify) + ?next= bieżącej strony (ścieżka i query, ten sam host); null na samej stronie telefonu.
  #phoneVerifyUrl(verifyUrl) {
    const base = verifyUrl || "/users/telefon/";
    if (window.location.pathname === base) return null; // already on the phone page: nowhere to send the buyer
    const here = `${window.location.pathname}${window.location.search}`;
    return `${base}?next=${encodeURIComponent(here)}`;
  }

  // Akceptacja regulaminu z modala checkout → zapis akceptacji, zamknięcie modala, ponowny checkout.
  async acceptTerms() {
    if (this.acceptingTerms || this.checkingOut) return;
    this.acceptingTerms = true;
    this.#updatePurchaseControls();
    const res = await sendJSON("POST", this.acceptTermsUrlValue, {});
    this.acceptingTerms = false;
    this.#updatePurchaseControls();
    if (!res.ok) {
      this.#flashError("Nie udało się zapisać akceptacji regulaminu.");
      return;
    }
    if (this.hasTermsModalTarget) this.termsModalTarget.hidden = true;
    await this.checkout();
  }

  closeTerms() {
    if (this.hasTermsModalTarget) this.termsModalTarget.hidden = true;
  }

  // Klik w treść modala nie propaguje do overlaya (który zamyka modal).
  stop(event) {
    event.stopPropagation();
  }

  countValueChanged(value) {
    this.countTargets.forEach((el) => {
      el.textContent = value;
    });
  }

  // --- prywatne ---

  #render(data) {
    this.countValue = data.count;
    if (this.hasErrorTarget) this.errorTarget.hidden = true; // udany render czyści stary komunikat błędu
    if (this.hasNetTarget) this.netTarget.textContent = formatPln(data.net);
    if (this.hasVatTarget) this.vatTarget.textContent = formatPln(data.vat);
    if (this.hasGrossTarget)
      this.grossTarget.textContent = formatPln(data.gross);

    if (this.hasItemsTarget) {
      this.itemsTarget.textContent = "";
      const template = document.getElementById("cart-item-template");
      for (const item of listResults(data)) {
        const row = template.content.firstElementChild.cloneNode(true);
        row.dataset.cartItem = item.id;
        row.querySelector('[data-field="cat"]').textContent =
          item.category_path;
        row.querySelector('[data-field="code"]').textContent = item.code;
        row.querySelector('[data-field="price"]').textContent = formatPln(
          item.price_pln,
        );
        // ins-slots#7: mode label (Slot / Wyłączność).
        // WHY conditional: the cart API currently returns only RESERVED listings (exclusivity)
        // and does NOT include a `mode` field — slot reservations live in SlotReservation and
        // are NOT yet surfaced by list_cart. Leave the element hidden when mode is absent rather
        // than inferring "exclusivity" from position — a wrong label is worse than no label.
        // The element unhides automatically once the backend adds the field.
        const modeEl = row.querySelector('[data-field="mode"]');
        if (modeEl && item.mode) {
          modeEl.textContent = item.mode === "slot" ? "Slot" : "Wyłączność";
          modeEl.hidden = false;
        }
        const remove = row.querySelector("[data-action='cart#remove']");
        remove.setAttribute("data-cart-id-param", item.id);
        remove.setAttribute("data-cart-lead-param", item.lead_id);
        remove.setAttribute("data-cart-subcategory-param", item.subcategory_id);
        this.itemsTarget.appendChild(row);
      }
    }
    if (!this.showingSuccess)
      this.#showState(data.count > 0 ? "content" : "empty");
  }

  #reflectButton(btn, inCart) {
    btn.classList.toggle("is-in-cart", inCart);
    const glyph = btn.querySelector(".ls-mono");
    const label = btn.querySelector("[data-cart-label]");
    if (glyph) glyph.textContent = inCart ? "✓" : "+";
    if (label) label.textContent = inCart ? "W koszyku" : "Dodaj";
    const row = btn.closest(".ls-lead-row");
    if (row) row.classList.toggle("is-selected", inCart);
  }

  #showState(state) {
    if (this.hasEmptyTarget) this.emptyTarget.hidden = state !== "empty";
    if (this.hasContentTarget) this.contentTarget.hidden = state !== "content";
    if (this.hasSuccessTarget) this.successTarget.hidden = state !== "success";
  }

  // Returns true when the digital-consent box is checked, or when the target is absent
  // (fixtures / legacy pages without the checkbox — do not block checkout there).
  #consentGiven() {
    return !this.hasDigitalConsentTarget || this.digitalConsentTarget.checked;
  }

  #updatePurchaseControls() {
    const busy = this.checkingOut || this.acceptingTerms;
    if (this.hasCheckoutBtnTarget) this.checkoutBtnTarget.disabled = busy || !this.#consentGiven();
    if (this.hasAcceptTermsBtnTarget) this.acceptTermsBtnTarget.disabled = busy;
  }

  #flashError(message) {
    if (!this.hasErrorTarget) return;
    this.errorTarget.textContent = message;
    this.errorTarget.hidden = false;
  }

  // Otwiera modal wyboru trybu. Wypełnia ceny/liczbę slotów, pokazuje animację.
  // type: "single" | "bulk".  exclAvailable: false = excl opcja ukryta (State 2 z ins-slots).
  #openMode({ type, lead, subcategory, listingIds, addBulkUrl, onDone, exclPrice, slotPrice, remaining, exclAvailable, btn }) {
    this._pendingMode = { type, lead, subcategory, listingIds, addBulkUrl, onDone, btn };

    // Liczba leadów — widoczna tylko dla bulk.
    const isBulk = type === "bulk";
    if (this.hasModeCountTarget) {
      this.modeCountTarget.hidden = !isBulk;
      if (isBulk) this.modeCountTarget.textContent = `${listingIds.length} leadów`;
    }

    // Ceny i dostępność opcji.
    if (this.hasModeOptExclTarget) {
      const priceEl = this.modeOptExclTarget.querySelector("[data-field='modalExclPrice']");
      if (priceEl) priceEl.textContent = exclPrice != null ? formatPln(exclPrice) : "";
      // State 2: excl zablokowane przez płatny slot — ukryj opcję.
      this.modeOptExclTarget.hidden = !exclAvailable;
      this.modeOptExclTarget.classList.toggle("is-active", !!exclAvailable);
    }
    if (this.hasModeOptSlotTarget) {
      const slotPriceEl = this.modeOptSlotTarget.querySelector("[data-field='modalSlotPrice']");
      if (slotPriceEl) slotPriceEl.textContent = slotPrice != null ? formatPln(slotPrice) : "";
      const remainEl = this.modeOptSlotTarget.querySelector("[data-field='modalSlotsRemaining']");
      if (remainEl) remainEl.textContent = remaining != null ? `${remaining} dostępne` : "";
      // Slot aktywny domyślnie gdy excl niedostępne (State 2).
      this.modeOptSlotTarget.classList.toggle("is-active", !exclAvailable);
    }

    // Pokaż overlay i dodaj is-open w następnej klatce (fade+scale — patrz preview_controller.js).
    if (!this.hasModeModalTarget) return;
    this.modeModalTarget.hidden = false;
    if (this._modeRaf) cancelAnimationFrame(this._modeRaf);
    this._modeRaf = requestAnimationFrame(() => {
      if (this.hasModeModalTarget) this.modeModalTarget.classList.add("is-open");
    });
  }
}
